import type { ICockpitRuntimeConfig } from '@/types';
import type { IUser } from '@condo/domain';
import { buildSentryUserContext } from '@condo/helpers';
import * as Sentry from '@sentry/vue';
import type { App } from 'vue';

export const initSentry = (config: ICockpitRuntimeConfig, app: App, appContext: string) => {
    if (!config.SENTRY_ENABLED) {
        return;
    }
    Sentry.init({
        app,
        debug: config.SENTRY_DEBUG,
        dsn: config.SENTRY_DSN,
        environment: config.ENVIRONMENT_NAME,
        release: `${config.HOUSEHOLD_VERSION}-core`,
        enabled: true,
        ignoreErrors: [
            // @note strings are partial matches in sentry
            'ChunkLoadError',
            'ResizeObserver loop completed with undelivered notification',
            /code 4\d{2}/,
            'Importing a module script failed',
            /Failed to fetch dynamically imported module/gi,
            /is not a valid JavaScript MIME type/gi,
            /NavigationDuplicated/gi,
            /Network/gi,
            'Unexpected token',
            /Navigation cancelled/gi,
            /Unable to preload CSS/gi,
            /^Loading chunk.*failed$/gi,
            /^Loading CSS chunk.*failed$/gi,
            /Could not find a 3d context/gi,
        ],
        integrations: [Sentry.replayIntegration(), Sentry.browserTracingIntegration()],
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
    Sentry.setTag('server_name', config.SERVICE_NAME);
    Sentry.setTag('app_context', appContext);
};

export const setSentryUser = (user: IUser) => {
    const { user: sentryUser, tags } = buildSentryUserContext(user);
    Sentry.setUser(sentryUser ?? null);
    if (tags) {
        for (const [key, value] of Object.entries(tags)) {
            Sentry.setTag(key, value);
        }
    }
};
